import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
    Smartphone, Activity, Heart, Pill, Stethoscope, Building2, UsersRound,
    ArrowRight, Sparkles, Download, Video, FileText, Shield, Clock, CheckCircle2,
} from "lucide-react";

/**
 * Mobile Application — hero + three dashboard mockups (Patient · Doctor · Employer HR)
 * + two conversion banners (Doctor-register & Employer-register).
 *
 * Dashboard "screenshots" are built as styled JSX mockups rather than real images —
 * cleaner, faster to load, and always in sync with the actual product UI.
 */

export default function MobileApp() {
    const { t } = useTranslation();

    return (
        <div className="bg-white min-h-screen" data-testid="mobile-app-page">
            <Navbar />
            <main className="pt-28 pb-20">
                {/* ---------- Hero ---------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-4">
                                {t("mobileApp.eyebrow")}
                            </div>
                            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter leading-[0.95] mb-5">
                                {t("mobileApp.titleLine1")}
                                <br />
                                <span className="brand-gradient-text">{t("mobileApp.titleLine2")}</span>
                            </h1>
                            <p className="text-lg text-gray-600 max-w-2xl leading-relaxed mb-7">
                                {t("mobileApp.intro")}
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid="mobile-app-signup-cta">
                                    <Download className="w-4 h-4" /> {t("mobileApp.cta.getStarted")}
                                </Link>
                                <a href="#doctors" className="btn-outline inline-flex items-center gap-2" data-testid="mobile-app-doctors-anchor">
                                    <Stethoscope className="w-4 h-4" /> {t("mobileApp.cta.forDoctors")}
                                </a>
                                <a href="#hr" className="btn-outline inline-flex items-center gap-2" data-testid="mobile-app-hr-anchor">
                                    <UsersRound className="w-4 h-4" /> {t("mobileApp.cta.forHR")}
                                </a>
                            </div>
                            <div className="mt-8 flex flex-wrap gap-5 text-xs text-gray-500 font-mono tracking-wider">
                                <span className="inline-flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-[var(--medx-red)]" /> {t("mobileApp.badges.encrypted")}</span>
                                <span className="inline-flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[var(--medx-orange)]" /> {t("mobileApp.badges.sync")}</span>
                                <span className="inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[var(--medx-red)]" /> {t("mobileApp.badges.aiReports")}</span>
                            </div>
                        </div>

                        {/* Floating phone-mockup trio preview */}
                        <div className="lg:col-span-5 relative h-[520px] hidden lg:block" data-testid="mobile-app-hero-mockups">
                            <PhoneMockup rotate={-10} offsetX={-30} offsetY={30} z={1}>
                                <DoctorMiniDashboard />
                            </PhoneMockup>
                            <PhoneMockup rotate={0} offsetX={70} offsetY={0} z={2}>
                                <PatientMiniDashboard />
                            </PhoneMockup>
                            <PhoneMockup rotate={10} offsetX={170} offsetY={30} z={1}>
                                <EmployerMiniDashboard />
                            </PhoneMockup>
                        </div>
                    </div>
                </section>

                {/* ---------- Patient Dashboard screenshot ---------- */}
                <ScreenSection
                    reversed={false}
                    pill={t("mobileApp.patient.pill")}
                    title={t("mobileApp.patient.title")}
                    body={t("mobileApp.patient.body")}
                    bullets={[
                        t("mobileApp.patient.b1"),
                        t("mobileApp.patient.b2"),
                        t("mobileApp.patient.b3"),
                        t("mobileApp.patient.b4"),
                    ]}
                    dashboard={<PatientDashboardMockup />}
                    testId="patient-screen"
                />

                {/* ---------- Doctor Dashboard screenshot ---------- */}
                <ScreenSection
                    reversed={true}
                    pill={t("mobileApp.doctor.pill")}
                    title={t("mobileApp.doctor.title")}
                    body={t("mobileApp.doctor.body")}
                    bullets={[
                        t("mobileApp.doctor.b1"),
                        t("mobileApp.doctor.b2"),
                        t("mobileApp.doctor.b3"),
                        t("mobileApp.doctor.b4"),
                    ]}
                    dashboard={<DoctorDashboardMockup />}
                    testId="doctor-screen"
                />

                {/* ---------- Employer HR Dashboard screenshot ---------- */}
                <ScreenSection
                    reversed={false}
                    pill={t("mobileApp.hr.pill")}
                    title={t("mobileApp.hr.title")}
                    body={t("mobileApp.hr.body")}
                    bullets={[
                        t("mobileApp.hr.b1"),
                        t("mobileApp.hr.b2"),
                        t("mobileApp.hr.b3"),
                        t("mobileApp.hr.b4"),
                    ]}
                    dashboard={<EmployerDashboardMockup />}
                    testId="hr-screen"
                />

                {/* ---------- Doctor CTA banner ---------- */}
                <section id="doctors" className="max-w-7xl mx-auto px-6 md:px-12 pt-6 pb-10">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#312e81] text-white p-10 md:p-14 grain" data-testid="doctor-cta-banner">
                        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[var(--medx-orange)]/30 blur-3xl" />
                        <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-[var(--medx-red)]/30 blur-3xl" />
                        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-8">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/40 backdrop-blur text-xs font-mono tracking-[0.18em] mb-5 text-white font-bold">
                                    <Stethoscope className="w-3.5 h-3.5 text-[var(--medx-orange)]" /> {t("mobileApp.doctorBanner.eyebrow")}
                                </div>
                                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mb-4 text-white" data-testid="doctor-banner-title">
                                    {t("mobileApp.doctorBanner.title")}
                                </h3>
                                <p className="text-white/80 max-w-2xl mb-5 leading-relaxed">
                                    {t("mobileApp.doctorBanner.body")}
                                </p>
                                <ul className="text-sm text-white/90 space-y-2 mb-6">
                                    <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[var(--medx-orange)]" /> {t("mobileApp.doctorBanner.p1")}</li>
                                    <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[var(--medx-orange)]" /> {t("mobileApp.doctorBanner.p2")}</li>
                                    <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[var(--medx-orange)]" /> {t("mobileApp.doctorBanner.p3")}</li>
                                </ul>
                            </div>
                            <div className="lg:col-span-4 flex flex-col gap-3">
                                <Link to="/doctor/signup" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--medx-orange)] text-white font-semibold hover:bg-[var(--medx-red)] transition shadow-lg" data-testid="mobile-app-doctor-register">
                                    <Stethoscope className="w-4 h-4" /> {t("mobileApp.doctorBanner.ctaRegister")}
                                </Link>
                                <Link to="/doctor/login" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/40 text-white font-semibold hover:bg-white/20 transition" data-testid="mobile-app-doctor-login">
                                    {t("mobileApp.doctorBanner.ctaLogin")} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ---------- HR / Employer CTA banner ---------- */}
                <section id="hr" className="max-w-7xl mx-auto px-6 md:px-12 pt-4">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-100 via-white to-red-50 border border-orange-100 p-10 md:p-14 grain" data-testid="hr-cta-banner">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-8">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--medx-red)]/10 border border-[var(--medx-red)]/30 text-xs font-mono tracking-[0.18em] text-[var(--medx-red)] mb-5">
                                    <Building2 className="w-3.5 h-3.5" /> {t("mobileApp.hrBanner.eyebrow")}
                                </div>
                                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mb-4">
                                    {t("mobileApp.hrBanner.title")}
                                </h3>
                                <p className="text-gray-700 max-w-2xl mb-5 leading-relaxed">
                                    {t("mobileApp.hrBanner.body")}
                                </p>
                                <ul className="text-sm text-gray-800 space-y-2 mb-6">
                                    <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[var(--medx-red)]" /> {t("mobileApp.hrBanner.p1")}</li>
                                    <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[var(--medx-red)]" /> {t("mobileApp.hrBanner.p2")}</li>
                                    <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[var(--medx-red)]" /> {t("mobileApp.hrBanner.p3")}</li>
                                </ul>
                            </div>
                            <div className="lg:col-span-4 flex flex-col gap-3">
                                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="mobile-app-hr-register">
                                    <Building2 className="w-4 h-4" /> {t("mobileApp.hrBanner.ctaRegister")}
                                </Link>
                                <Link to="/employer/login" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="mobile-app-hr-login">
                                    {t("mobileApp.hrBanner.ctaLogin")} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

// =================================================================
// Reusable section
// =================================================================
function ScreenSection({ reversed, pill, title, body, bullets, dashboard, testId }) {
    return (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-14" data-testid={testId}>
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${reversed ? "lg:[grid-template-areas:'r_r_r_r_r_r_r_r_l_l_l_l']" : ""}`}>
                <div className="lg:col-span-5" style={reversed ? { gridArea: "l" } : {}}>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 text-[var(--medx-red)] text-xs font-mono tracking-[0.18em] mb-4">
                        {pill}
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mb-4">{title}</h2>
                    <p className="text-gray-600 leading-relaxed mb-5">{body}</p>
                    <ul className="space-y-2 text-sm">
                        {bullets.map((b, i) => (
                            <li key={i} className="flex gap-2 items-start text-gray-700">
                                <CheckCircle2 className="w-4 h-4 text-[var(--medx-red)] mt-0.5 shrink-0" /> {b}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="lg:col-span-7" style={reversed ? { gridArea: "r" } : {}}>
                    {dashboard}
                </div>
            </div>
        </section>
    );
}

// =================================================================
// Phone-mockup frames
// =================================================================
function PhoneMockup({ rotate = 0, offsetX = 0, offsetY = 0, z = 1, children }) {
    return (
        <div
            className="absolute left-1/2 top-6 w-[220px] h-[460px] rounded-[36px] bg-black p-2 shadow-2xl"
            style={{
                transform: `translate(calc(-50% + ${offsetX}px), ${offsetY}px) rotate(${rotate}deg)`,
                zIndex: z,
            }}
        >
            <div className="w-full h-full rounded-[28px] bg-white overflow-hidden relative">
                {/* notch */}
                <div className="absolute left-1/2 -translate-x-1/2 top-2 w-16 h-5 bg-black rounded-full z-10" />
                {children}
            </div>
        </div>
    );
}

// =================================================================
// Mini mockups (used in hero stack)
// =================================================================
function PatientMiniDashboard() {
    return (
        <div className="h-full pt-10 bg-gradient-to-b from-orange-50 to-white">
            <div className="px-4">
                <div className="text-[9px] font-mono text-gray-500 mb-1">PATIENT</div>
                <div className="text-sm font-bold">Hi, Kelvin 👋</div>
                <div className="relative w-24 h-24 mx-auto mt-5">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] blur-md opacity-70" />
                    <div className="relative w-full h-full rounded-full bg-white grid place-items-center shadow border border-orange-100">
                        <div className="text-center">
                            <div className="text-xl font-bold brand-gradient-text">82</div>
                            <div className="text-[8px] text-gray-500 font-mono">HEALTH</div>
                        </div>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-1.5 text-[8px]">
                    <MiniStat label="BP" value="124/82" />
                    <MiniStat label="HR" value="78 bpm" />
                    <MiniStat label="SpO₂" value="98 %" />
                    <MiniStat label="Temp" value="36.8°" />
                </div>
            </div>
        </div>
    );
}
function DoctorMiniDashboard() {
    return (
        <div className="h-full pt-10 bg-gradient-to-b from-[#0b1021] to-[#1e293b] text-white">
            <div className="px-4">
                <div className="text-[9px] font-mono text-white/50 mb-1">DOCTOR</div>
                <div className="text-sm font-bold">Dr. N. Khumalo</div>
                <div className="mt-3 text-[8px] font-mono text-emerald-300">● ONLINE</div>
                <div className="mt-4 space-y-1.5">
                    <QueueRow name="J. Smith" tag="New" />
                    <QueueRow name="K. Mokoena" tag="Vitals" />
                    <QueueRow name="S. Khan" tag="Follow-up" />
                </div>
                <div className="mt-3 text-[8px] text-white/60 font-mono">3 in queue</div>
            </div>
        </div>
    );
}
function EmployerMiniDashboard() {
    return (
        <div className="h-full pt-10 bg-gradient-to-b from-red-50 to-white">
            <div className="px-4">
                <div className="text-[9px] font-mono text-gray-500 mb-1">HR</div>
                <div className="text-sm font-bold">Newmoon Media</div>
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                    <KpiMini value="50" label="Staff" />
                    <KpiMini value="81" label="Score" tone="red" />
                    <KpiMini value="5" label="Urgent" tone="red" />
                    <KpiMini value="32" label="Good" tone="green" />
                </div>
                <div className="mt-3 h-10 rounded-md bg-gradient-to-r from-red-100 to-orange-100" />
            </div>
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="rounded-md bg-white border border-orange-100 p-1.5 shadow-sm">
            <div className="text-[7px] font-mono text-gray-500">{label}</div>
            <div className="font-bold text-gray-900">{value}</div>
        </div>
    );
}
function QueueRow({ name, tag }) {
    return (
        <div className="flex items-center justify-between text-[8px] bg-white/10 rounded px-2 py-1">
            <span>{name}</span>
            <span className="font-mono text-amber-300">{tag}</span>
        </div>
    );
}
function KpiMini({ value, label, tone = "neutral" }) {
    const toneColor = tone === "red" ? "text-[var(--medx-red)]" : tone === "green" ? "text-emerald-600" : "text-gray-900";
    return (
        <div className="rounded-md bg-white border border-orange-100 p-1.5 shadow-sm">
            <div className={`font-bold ${toneColor}`}>{value}</div>
            <div className="text-[7px] font-mono text-gray-500">{label}</div>
        </div>
    );
}

// =================================================================
// Full-size dashboard mockups
// =================================================================
function PatientDashboardMockup() {
    return (
        <div className="rounded-3xl bg-gradient-to-br from-orange-50 via-white to-red-50 border border-orange-100 shadow-xl p-6">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="text-[10px] font-mono text-gray-500">PATIENT DASHBOARD</div>
                    <div className="font-display text-xl font-bold">Hi Kelvin 👋</div>
                </div>
                <div className="text-xs text-gray-500 font-mono">LAST SCAN · 2h ago</div>
            </div>
            <div className="grid grid-cols-12 gap-4">
                {/* Halo */}
                <div className="col-span-5 flex items-center justify-center">
                    <div className="relative w-44 h-44">
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] blur-xl opacity-60" />
                        <div className="relative w-full h-full rounded-full bg-white grid place-items-center shadow-xl border border-orange-100">
                            <div className="text-center">
                                <div className="font-display text-5xl font-bold brand-gradient-text">82</div>
                                <div className="text-[10px] text-gray-500 font-mono tracking-wider">HEALTH SCORE</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-span-7 grid grid-cols-2 gap-3">
                    <KpiCard icon={Heart} value="124/82" label="Blood Pressure" />
                    <KpiCard icon={Activity} value="78 bpm" label="Heart Rate" />
                    <KpiCard icon={Pill} value="98%" label="SpO₂" />
                    <KpiCard icon={FileText} value="2" label="New Rx" />
                </div>
            </div>
            <div className="mt-5 rounded-xl bg-white border border-gray-100 p-4">
                <div className="text-[10px] font-mono text-gray-500 mb-2">HEALTH TREND · LAST 30 DAYS</div>
                <SparkChart color="#e63946" />
            </div>
            <div className="mt-4 flex gap-2">
                <TabPill icon={Activity} label="Vitals" active />
                <TabPill icon={FileText} label="Medical Records" />
                <TabPill icon={Sparkles} label="AI Report" />
                <TabPill icon={Video} label="Consult" />
            </div>
        </div>
    );
}

function DoctorDashboardMockup() {
    return (
        <div className="rounded-3xl bg-gradient-to-br from-[#0b1021] via-[#121a36] to-[#0f172a] text-white border border-white/10 shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="text-[10px] font-mono text-white/60">DOCTOR PORTAL</div>
                    <div className="font-display text-xl font-bold">Dr. Naledi Khumalo</div>
                </div>
                <div className="inline-flex items-center gap-1.5 text-[10px] font-mono bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full border border-emerald-400/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> ONLINE
                </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
                <DarkKpi value="12" label="Today" />
                <DarkKpi value="3" label="Awaiting PIN" />
                <DarkKpi value="98%" label="Avg Rating" />
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold">Patient queue</div>
                    <div className="text-[10px] font-mono text-white/50">3 ACTIVE</div>
                </div>
                <QueueCard name="J. Smith" tag="New vitals" age="34, M" tone="orange" />
                <QueueCard name="K. Mokoena" tag="Awaiting PIN" age="52, F" tone="amber" />
                <QueueCard name="S. Khan" tag="Follow-up" age="47, M" tone="emerald" />
            </div>
        </div>
    );
}

function EmployerDashboardMockup() {
    return (
        <div className="rounded-3xl bg-white border border-orange-100 shadow-xl p-6">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="text-[10px] font-mono text-gray-500">EMPLOYER DASHBOARD</div>
                    <div className="font-display text-xl font-bold">Newmoon Media · HR</div>
                </div>
                <div className="text-xs text-gray-500 font-mono">50 EMPLOYEES</div>
            </div>
            <div className="grid grid-cols-4 gap-3 mb-5">
                <KpiCard icon={UsersRound} value="50" label="Workforce" tone="orange" />
                <KpiCard icon={Activity} value="81" label="Avg Score" tone="red" />
                <KpiCard icon={Heart} value="5" label="Urgent Leave" tone="red" />
                <KpiCard icon={CheckCircle2} value="32" label="Optimal" tone="green" />
            </div>
            <div className="rounded-xl bg-gradient-to-r from-orange-50 to-red-50 border border-orange-100 p-4">
                <div className="text-[10px] font-mono text-gray-500 mb-2">WELLNESS DISTRIBUTION</div>
                <div className="flex gap-1 h-5 rounded-full overflow-hidden">
                    <div className="bg-emerald-500" style={{ width: "64%" }} />
                    <div className="bg-amber-500" style={{ width: "26%" }} />
                    <div className="bg-[var(--medx-red)]" style={{ width: "10%" }} />
                </div>
                <div className="mt-2 flex justify-between text-[10px] text-gray-600 font-mono">
                    <span>32 Optimal</span>
                    <span>13 Break</span>
                    <span>5 Urgent</span>
                </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] text-gray-500 font-mono">
                <span>■ T. Mokoena · 92</span>
                <span>■ S. Van der M. · 78</span>
                <span>■ L. Nkosi · 61</span>
            </div>
        </div>
    );
}

// -------- shared bits --------
function KpiCard({ icon: Icon, value, label, tone = "neutral" }) {
    const toneBg = tone === "red" ? "from-rose-50 to-red-100" : tone === "green" ? "from-emerald-50 to-emerald-100" : tone === "orange" ? "from-orange-50 to-amber-100" : "from-gray-50 to-white";
    const toneTxt = tone === "red" ? "text-[var(--medx-red)]" : tone === "green" ? "text-emerald-700" : tone === "orange" ? "text-amber-700" : "text-gray-900";
    return (
        <div className={`rounded-xl bg-gradient-to-br ${toneBg} border border-white p-3 shadow-sm`}>
            <Icon className={`w-4 h-4 ${toneTxt} mb-2`} />
            <div className={`font-display text-xl font-bold ${toneTxt}`}>{value}</div>
            <div className="text-[10px] font-mono text-gray-500 mt-0.5">{label}</div>
        </div>
    );
}
function DarkKpi({ value, label }) {
    return (
        <div className="rounded-xl bg-white/5 border border-white/10 p-3">
            <div className="font-display text-2xl font-bold text-white">{value}</div>
            <div className="text-[9px] font-mono text-white/60 mt-0.5">{label}</div>
        </div>
    );
}
function QueueCard({ name, tag, age, tone }) {
    const bg = tone === "orange" ? "bg-[var(--medx-orange)]/20 text-amber-300" : tone === "amber" ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300";
    return (
        <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 text-xs">
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-white/10 grid place-items-center text-[10px] font-bold">{name[0]}</div>
                <div>
                    <div className="font-semibold">{name}</div>
                    <div className="text-[9px] text-white/50 font-mono">{age}</div>
                </div>
            </div>
            <span className={`text-[9px] font-mono px-2 py-0.5 rounded-full ${bg}`}>{tag}</span>
        </div>
    );
}
function TabPill({ icon: Icon, label, active }) {
    return (
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium ${active ? "bg-[var(--medx-red)] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
            <Icon className="w-3 h-3" /> {label}
        </div>
    );
}
function SparkChart({ color = "#e63946" }) {
    // Static but animated-looking SVG sparkline
    return (
        <svg viewBox="0 0 300 80" className="w-full h-16">
            <defs>
                <linearGradient id="sg" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d="M0,55 L25,48 L50,52 L75,38 L100,42 L125,30 L150,34 L175,24 L200,28 L225,20 L250,24 L275,16 L300,22 L300,80 L0,80 Z" fill="url(#sg)" />
            <path d="M0,55 L25,48 L50,52 L75,38 L100,42 L125,30 L150,34 L175,24 L200,28 L225,20 L250,24 L275,16 L300,22" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
        </svg>
    );
}
